/**
 * WS-Y: "ship the mock-ups only" — ONE switch, OFF by default.
 *
 * An export used to carry a `Print/` folder beside the mock-ups: the customer's uploaded
 * artwork, or (native, WS-H2 / D-10) the back print area re-rendered at
 * `render_settings.print_dpi`. Print files are not being shipped for now, so nothing writes
 * one — not the run's write-through writer, not a bundle, not a zip, not "Copy run to…".
 *
 * The print-file code path is INTACT behind this flag rather than deleted: the derivation
 * (`export/bundle.ts::printFilesFromOutputs`), the render (`batch/run.ts`), the writer branch
 * (`export/runWriter.ts`) and the copy (`export/copyRun.ts`) all still exist and all still have
 * their tests — they simply do not run with the switch off. Turning it back on is one value,
 * not a re-implementation.
 *
 * Device-local (localStorage, never synced), the same shape as `renderFallback.ts` and
 * `renderBackend.ts`. It is NOT exposed in Settings yet — deliberately, because "off for now"
 * is a product decision and not a per-operator preference. When it is exposed it belongs in
 * Settings → Rendering (`settings/SettingsPanel.tsx`, beside `setting-primary-view-fallback`),
 * which is where the other device-local render/export switches already live.
 *
 * Every read is try/catch'd and falls back to OFF: a browser with storage disabled must ship
 * mock-ups only, never quietly resurrect the folder this wave removed.
 */
const KEY = "genz.export.printFiles";
export const EXPORT_PRINT_FILES_KEY = KEY;

/** Do exports carry print files? `false` unless this device explicitly turned them back on. */
export function getExportPrintFiles(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false; // private mode / no storage — the default (off) is the shipping one
  }
}

export function setExportPrintFiles(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, "1");
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode / no storage — the default (off) keeps applying */
  }
}
